import React, { useEffect, useMemo, useState } from "react";
import { changePassword } from "../services/api";

function RuleIcon({ ok }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 16,
        height: 16,
        borderRadius: "999px",
        marginRight: 6,
        fontSize: 11,
        fontWeight: 800,
        border: ok ? "none" : "1px solid #d1d5db",
        background: ok ? "#16a34a" : "#f9fafb",
        color: ok ? "#ffffff" : "#9ca3af",
      }}
    >
      {ok ? "✓" : "•"}
    </span>
  );
}

export default function ChangePasswordModal({ open, onClose }) {
  const [pwCurrent, setPwCurrent] = useState("");
  const [pwNew, setPwNew] = useState("");
  const [pwNewConfirm, setPwNewConfirm] = useState("");
  const [pwBusy, setPwBusy] = useState(false);
  const [pwError, setPwError] = useState(null);
  const [pwSuccess, setPwSuccess] = useState(null);

  // Reset when opening
  useEffect(() => {
    if (!open) return;
    setPwCurrent("");
    setPwNew("");
    setPwNewConfirm("");
    setPwBusy(false);
    setPwError(null);
    setPwSuccess(null);
  }, [open]);

  const rules = useMemo(() => {
    const minLenOk = pwNew.length >= 10;
    const hasLower = /[a-z]/.test(pwNew);
    const hasUpper = /[A-Z]/.test(pwNew);
    const hasDigit = /[0-9]/.test(pwNew);
    const differentFromCurrent =
      pwNew.length > 0 && pwCurrent.length > 0 && pwNew !== pwCurrent;

    const allOk =
      minLenOk && hasLower && hasUpper && hasDigit && differentFromCurrent;

    const confirmMatches = pwNewConfirm.length > 0 && pwNewConfirm === pwNew;

    return {
      minLenOk,
      hasLower,
      hasUpper,
      hasDigit,
      differentFromCurrent,
      allOk,
      confirmMatches,
    };
  }, [pwNew, pwNewConfirm, pwCurrent]);

  const close = () => {
    if (pwBusy) return;
    onClose?.();
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setPwError(null);
    setPwSuccess(null);

    if (!pwCurrent || !pwNew || !pwNewConfirm) {
      setPwError("Please fill in all password fields.");
      return;
    }
    if (pwNew !== pwNewConfirm) {
      setPwError("New password and confirmation do not match.");
      return;
    }
    if (!rules.minLenOk) {
      setPwError("New password must be at least 10 characters long.");
      return;
    }
    if (pwNew === pwCurrent) {
      setPwError("New password cannot be the same as the current password.");
      return;
    }
    if (!rules.hasLower) {
      setPwError("New password must contain at least one lowercase letter.");
      return;
    }
    if (!rules.hasUpper) {
      setPwError("New password must contain at least one uppercase letter.");
      return;
    }
    if (!rules.hasDigit) {
      setPwError("New password must contain at least one digit.");
      return;
    }

    setPwBusy(true);
    try {
      await changePassword(pwCurrent, pwNew, pwNewConfirm);
      setPwSuccess(
        "Password changed successfully. You will use the new password next time you log in."
      );
      setPwCurrent("");
      setPwNew("");
      setPwNewConfirm("");

      setTimeout(() => close(), 1600);
    } catch (err) {
      setPwError(err?.message || "Failed to change password.");
    } finally {
      setPwBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="modal-backdrop"
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => {
        // click outside closes
        if (e.target === e.currentTarget) close();
      }}
    >
      <div className="modal-card" style={{ maxWidth: 420, width: "100%" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 12,
          }}
        >
          <h3 style={{ margin: 0, color: "#111827" }}>Change password</h3>
          <button
            className="btn-secondary"
            type="button"
            onClick={close}
            disabled={pwBusy}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <form onSubmit={onSubmit}>
          <div style={{ marginBottom: 10 }}>
            <label
              htmlFor="current-password"
              style={{ display: "block", marginBottom: 4, fontWeight: 700 }}
            >
              Current password
            </label>
            <input
              id="current-password"
              type="password"
              className="fld"
              value={pwCurrent}
              onChange={(e) => setPwCurrent(e.target.value)}
              disabled={pwBusy}
              autoFocus
            />
          </div>

          <div style={{ marginBottom: 10 }}>
            <label
              htmlFor="new-password"
              style={{ display: "block", marginBottom: 4, fontWeight: 700 }}
            >
              New password
            </label>
            <input
              id="new-password"
              type="password"
              className="fld"
              value={pwNew}
              onChange={(e) => setPwNew(e.target.value)}
              disabled={pwBusy}
            />

            <div
              style={{
                marginTop: 6,
                fontSize: 12,
                color: "#4b5563",
                lineHeight: 1.5,
                background: "#f9fafb",
                borderRadius: 8,
                padding: "8px 10px",
                border: "1px dashed #e5e7eb",
              }}
            >
              <div
                style={{
                  fontWeight: 800,
                  marginBottom: 6,
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <span>Password requirements</span>
                {pwNew ? (
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 800,
                      color: rules.allOk ? "#16a34a" : "#9ca3af",
                    }}
                  >
                    {rules.allOk ? "All rules satisfied" : "Typing…"}
                  </span>
                ) : null}
              </div>

              <ul
                style={{
                  margin: 0,
                  paddingLeft: 0,
                  listStyle: "none",
                  display: "grid",
                  gap: 2,
                }}
              >
                <li style={{ display: "flex", alignItems: "center" }}>
                  <RuleIcon ok={rules.minLenOk} />
                  <span>At least 10 characters long</span>
                </li>
                <li style={{ display: "flex", alignItems: "center" }}>
                  <RuleIcon ok={rules.hasLower} />
                  <span>At least one lowercase letter</span>
                </li>
                <li style={{ display: "flex", alignItems: "center" }}>
                  <RuleIcon ok={rules.hasUpper} />
                  <span>At least one uppercase letter</span>
                </li>
                <li style={{ display: "flex", alignItems: "center" }}>
                  <RuleIcon ok={rules.hasDigit} />
                  <span>At least one digit</span>
                </li>
                <li style={{ display: "flex", alignItems: "center" }}>
                  <RuleIcon ok={rules.differentFromCurrent} />
                  <span>Different from your current password</span>
                </li>
              </ul>
            </div>
          </div>

          <div style={{ marginBottom: 10 }}>
            <label
              htmlFor="new-password-confirm"
              style={{ display: "block", marginBottom: 4, fontWeight: 700 }}
            >
              Confirm new password
            </label>
            <input
              id="new-password-confirm"
              type="password"
              className="fld"
              value={pwNewConfirm}
              onChange={(e) => setPwNewConfirm(e.target.value)}
              disabled={pwBusy}
            />

            <div
              style={{
                marginTop: 6,
                fontSize: 12,
                display: "flex",
                alignItems: "center",
                color: pwNewConfirm
                  ? rules.confirmMatches
                    ? "#16a34a"
                    : "#b91c1c"
                  : "#6b7280",
              }}
            >
              <RuleIcon ok={rules.confirmMatches && !!pwNewConfirm} />
              {pwNewConfirm
                ? rules.confirmMatches
                  ? "Passwords match."
                  : "Passwords do not match yet."
                : "Re-type your new password to confirm."}
            </div>
          </div>

          {pwError ? (
            <div style={{ marginBottom: 10, fontSize: 13, color: "#b91c1c" }}>
              {pwError}
            </div>
          ) : null}

          {pwSuccess ? (
            <div style={{ marginBottom: 10, fontSize: 13, color: "#16a34a" }}>
              {pwSuccess}
            </div>
          ) : null}

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 8,
              marginTop: 8,
            }}
          >
            <button
              type="button"
              className="btn-secondary"
              onClick={close}
              disabled={pwBusy}
            >
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={pwBusy}>
              {pwBusy ? "Saving…" : "Save new password"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
